// Two prepared decoders composite colour-over-matte MP4s into one transparent
// character. Keep the outgoing frame until the incoming video can draw, then
// blend for 250ms. Decoded assets are bounded; page visibility pauses all work.
const VERTEX = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = vec2(a_position.x * 0.5 + 0.5, 0.5 - a_position.y * 0.5);
  gl_Position = vec4(a_position, 0.0, 1.0);
}`
const FRAGMENT = `
precision mediump float;
uniform sampler2D u_from;
uniform sampler2D u_to;
uniform float u_from_seam;
uniform float u_to_seam;
uniform float u_mix;
varying vec2 v_uv;
vec4 cutout(sampler2D frame, float seam) {
  vec3 colour = texture2D(frame, vec2(v_uv.x, min(v_uv.y * 0.5, seam))).rgb;
  float alpha = texture2D(frame, vec2(v_uv.x, max(0.5 + v_uv.y * 0.5, 1.0 - seam))).r;
  alpha = smoothstep(0.04, 0.96, alpha);
  return vec4(colour * alpha, alpha);
}
void main() {
  gl_FragColor = mix(cutout(u_from, u_from_seam), cutout(u_to, u_to_seam), u_mix);
}`

export class CutoutPlayer {
  static supported() {
    try { return Boolean(document.createElement("canvas").getContext("webgl")) }
    catch (_error) { return false }
  }

  constructor(canvas) {
    this.canvas = canvas
    this.gl = canvas.getContext("webgl", { premultipliedAlpha: true, alpha: true, antialias: false })
    if (!this.gl) throw new Error("no webgl")
    this.video = this._video()
    this._cache = new Map()
    this._setUp()
    this._tick = this._tick.bind(this)
  }

  _video() {
    const video = document.createElement("video")
    video.muted = true
    video.playsInline = true
    video.preload = "auto"
    return video
  }

  preload(url) {
    if (!url || url === this.url || this._cache.has(url) || this._stopped) return
    const video = this._video()
    video.src = url
    video.load()
    this._cache.set(url, video)
    while (this._cache.size > 2) {
      const [oldUrl, oldVideo] = this._cache.entries().next().value
      this._release(oldVideo)
      this._cache.delete(oldUrl)
    }
  }

  play(url, { once = false, onEnd = null, atBoundary = false } = {}) {
    if (!url || this._stopped || (url === this.url && !once)) return
    if (atBoundary && this._front && !this._front.video.ended) {
      this.preload(url)
      this._queued = { url, once, onEnd }
      return
    }
    this._queued = null
    this._cancelIncoming()
    if (this._front) this._front.video.onended = null
    const video = this._cache.get(url) || (!this._front ? this.video : this._video())
    this._cache.delete(url)
    if (video.getAttribute("src") !== url) video.src = url
    video.currentTime = 0
    const incoming = { video, url, once, onEnd, time: -1, texture: this._texture() }
    this.video = video // Consumers attach speech sync to the incoming decoder.
    this.url = url
    this._incoming = incoming
    video.onended = () => this._ended(incoming)
    video.onplaying = () => {
      if (this._incoming !== incoming || this._stopped || this._paused) return
      incoming.started = performance.now()
      this._loop()
    }
    video.onerror = () => {
      if (this._incoming !== incoming) return
      this._cancelIncoming()
      this.url = this._front?.url || null
      this.video = this._front?.video || this._video()
      if (this._front) {
        this._front.video.onended = () => this._ended(this._front)
        if (this._front.video.ended && !this._front.once) this._ended(this._front)
      }
      if (once) onEnd?.()
    }
    if (!this._paused) video.play().catch((error) => { if (error.name !== "AbortError") video.onerror?.() })
  }

  _ended(slot) {
    if (this._stopped || (slot !== this._front && slot !== this._incoming)) return
    if (this._queued) {
      const next = this._queued
      this._queued = null
      this.play(next.url, next)
    } else if (slot.once) {
      this.url = null
      slot.onEnd?.()
    } else {
      slot.video.currentTime = 0
      if (!this._paused) slot.video.play().catch(() => {})
    }
  }

  pause() {
    this._paused = true
    this._front?.video.pause()
    this._incoming?.video.pause()
    cancelAnimationFrame(this._frame)
    this._looping = false
  }

  resume() {
    if (this._stopped) return
    this._paused = false
    for (const slot of [this._front, this._incoming]) {
      if (slot && !slot.video.ended) slot.video.play().catch(() => {})
    }
    this._loop()
  }

  _release(video) {
    video.onplaying = video.onended = video.onerror = null
    video.pause()
    video.removeAttribute("src")
    video.load()
  }

  _dispose(slot) {
    if (!slot) return
    this._release(slot.video)
    this.gl.deleteTexture(slot.texture)
  }

  _cancelIncoming() {
    this._dispose(this._incoming)
    this._incoming = null
  }

  destroy() {
    this._stopped = true
    cancelAnimationFrame(this._frame)
    this._dispose(this._front)
    this._cancelIncoming()
    this._cache.forEach((video) => this._release(video))
    this._cache.clear()
    this.gl.deleteBuffer(this._buffer)
    this.gl.deleteProgram(this._program)
  }

  _texture() {
    const gl = this.gl
    const texture = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    return texture
  }

  _setUp() {
    const gl = this.gl
    const program = this._program = gl.createProgram()
    ;[[gl.VERTEX_SHADER, VERTEX], [gl.FRAGMENT_SHADER, FRAGMENT]].forEach(([type, source]) => {
      const shader = gl.createShader(type)
      gl.shaderSource(shader, source)
      gl.compileShader(shader)
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader))
      gl.attachShader(program, shader)
      gl.deleteShader(shader)
    })
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program))
    gl.useProgram(program)
    this._buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this._buffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW)
    const position = gl.getAttribLocation(program, "a_position")
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
    this._uniforms = Object.fromEntries(["from", "to", "from_seam", "to_seam", "mix"].map((key) => [key, gl.getUniformLocation(program, `u_${key}`)]))
    gl.clearColor(0, 0, 0, 0)
  }

  _loop() {
    if (this._looping || this._stopped || this._paused) return
    this._looping = true
    this._frame = requestAnimationFrame(this._tick)
  }

  _tick(now) {
    this._looping = false
    if (this._stopped || this._paused || document.visibilityState !== "visible") return
    this._draw(now)
    this._loop()
  }

  _upload(slot, unit) {
    const { gl } = this
    gl.activeTexture(gl.TEXTURE0 + unit)
    gl.bindTexture(gl.TEXTURE_2D, slot.texture)
    if (slot.time !== slot.video.currentTime) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, slot.video)
      slot.time = slot.video.currentTime
    }
  }

  _draw(now) {
    const { gl, canvas } = this
    const incoming = this._incoming?.video.readyState >= 2 && this._incoming.started != null ? this._incoming : null
    const outgoing = this._front
    if (!incoming && !outgoing) return
    const to = incoming || outgoing
    const from = outgoing || to
    const mix = incoming && outgoing ? Math.min(1, (now - incoming.started) / 250) : 1
    const ratio = window.devicePixelRatio || 1
    const requestedWidth = Math.max(1, canvas.clientWidth * ratio)
    const requestedHeight = Math.max(1, canvas.clientHeight * ratio)
    const scale = Math.min(1, 4096 / Math.max(requestedWidth, requestedHeight), Math.sqrt(8_000_000 / (requestedWidth * requestedHeight)))
    const width = Math.round(requestedWidth * scale), height = Math.round(requestedHeight * scale)
    if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height }
    gl.viewport(0, 0, width, height)
    try { this._upload(from, 0); this._upload(to, 1) } catch (_error) { return }
    gl.uniform1i(this._uniforms.from, 0)
    gl.uniform1i(this._uniforms.to, 1)
    gl.uniform1f(this._uniforms.from_seam, 0.5 - 1 / from.video.videoHeight)
    gl.uniform1f(this._uniforms.to_seam, 0.5 - 1 / to.video.videoHeight)
    gl.uniform1f(this._uniforms.mix, mix)
    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
    if (incoming && mix === 1) {
      this._front = incoming
      this._incoming = null
      this._dispose(outgoing)
    }
  }
}
